import { useDispatch } from "react-redux";
import { Box, Table } from "@mantine/core";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useIsFetching } from "@tanstack/react-query";
import {
  setPageDailyForOverlimit,
  setSizeDailyForOverlimit,
  useSelectEnabledDailyForOverlimit,
  useSelectPageDailyForOverlimit,
  useSelectReportsDailyForOverlimit,
  useSelectSizeDailyForOverlimit,
  useSelectTotalCountDailyForOverlimit,
} from "../../../../redux/features/reports/dailyForOverlimitSlice";
import { AppPagination } from "../../../../components/AppPagination/AppPagination";
import { TableSkeleton } from "../../../../components/Skeletons/TableSkeleton";
import { TableNoResults } from "../../../../components/TableNoResults/TableNoResults";
import { dateWithHourFormat } from "../../../../utils/dateFormat";

const COLUMN_COUNT = 7;
// Keep the first-load skeleton small. Rendering size=20 × 7 cols = 140 Mantine
// <Skeleton> nodes synchronously after the click is what was eating the click
// thread (1400ms+). Subsequent fetches still render `size` rows.
const INITIAL_SKELETON_ROWS = 8;

const DailyForOverlimitTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const data = useSelectReportsDailyForOverlimit();
  const page = useSelectPageDailyForOverlimit();
  const size = useSelectSizeDailyForOverlimit();
  const totalCount = useSelectTotalCountDailyForOverlimit();
  const enabled = useSelectEnabledDailyForOverlimit();

  const isFetching = useIsFetching({
    queryKey: ["get-daily-for-overlimit-report"],
  });

  const handlePageChange = (newPage) => {
    dispatch(setPageDailyForOverlimit(newPage - 1));
  };

  const handleSizeChange = (newSize) => {
    dispatch(setSizeDailyForOverlimit(newSize));
    dispatch(setPageDailyForOverlimit(0));
  };

  if (!enabled) {
    return null;
  }

  const skeletonRows = data.length === 0 ? INITIAL_SKELETON_ROWS : size;

  const rows =
    data.length === 0
      ? [<TableNoResults key="empty-state" colSpan={COLUMN_COUNT} />]
      : data.map((row, idx) => (
          <Table.Tr key={`${row.cid}-${idx}`}>
            <Table.Td>{row.cid}</Table.Td>
            <Table.Td>{row.uic}</Table.Td>
            <Table.Td>{row.product}</Table.Td>
            <Table.Td>{row.limit}</Table.Td>
            <Table.Td>
              {row.date ? dayjs(row.date).format(dateWithHourFormat) : ""}
            </Table.Td>
            <Table.Td>{row.status}</Table.Td>
            <Table.Td>{row.cidExists}</Table.Td>
          </Table.Tr>
        ));

  return (
    <>
      <Box pt="md" className="table-panel">
        <Table horizontalSpacing="md" highlightOnHover stickyHeader>
          <Table.Thead bg="var(--mantine-color-gray-1)">
            <Table.Tr>
              <Table.Th>{t("reportsDailyOverlimit.cid")}</Table.Th>
              <Table.Th>{t("reportsDailyOverlimit.uic")}</Table.Th>
              <Table.Th>{t("reportsDailyOverlimit.product")}</Table.Th>
              <Table.Th>{t("reportsDailyOverlimit.limit")}</Table.Th>
              <Table.Th>{t("reportsDailyOverlimit.dateTime")}</Table.Th>
              <Table.Th>{t("reportsDailyOverlimit.status")}</Table.Th>
              <Table.Th>{t("reportsDailyOverlimit.cidExists")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          {isFetching ? (
            <TableSkeleton cols={COLUMN_COUNT} length={skeletonRows} />
          ) : (
            <Table.Tbody>{rows}</Table.Tbody>
          )}
        </Table>
      </Box>
      {totalCount > 0 && (
        <AppPagination
          page={page}
          take={size}
          total={totalCount}
          onPageChange={handlePageChange}
          onTakeChange={handleSizeChange}
        />
      )}
    </>
  );
};

export default DailyForOverlimitTable;
